import type { CpgReport } from '@features/campaigns';
import type { Slide, TextProps } from '@features/powerpoint';
import { themeV2 } from '../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../utils/constants-v2';
import { contentSlideHeaderV2 } from '../../../../utils/slide-header-v2';
import { generateTableV2 } from '../../../../utils/build-table-v2';
import { formatters, type ValueInput } from '@utils/formattersV2';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';

const METRIC_LABEL_FONT_SIZE = 18;
const METRIC_VALUE_FONT_SIZE = 32;
const METRIC_DESCRIPTION_FONT_SIZE = 12;

const metricLabelOptions: TextProps['options'] = {
  color: themeV2.colors.inmarketGreen,
  fontFace: DEFAULT_FONT,
  fontSize: METRIC_LABEL_FONT_SIZE,
  bold: true,
  breakLine: true,
};

const metricValueOptions: TextProps['options'] = {
  color: themeV2.colors.metricBlue,
  fontFace: DEFAULT_FONT,
  fontSize: METRIC_VALUE_FONT_SIZE,
  bold: true,
  breakLine: true,
};

const metricDescriptionOptions: TextProps['options'] = {
  color: themeV2.colors.metricBlue,
  fontFace: DEFAULT_FONT,
  fontSize: METRIC_DESCRIPTION_FONT_SIZE,
  breakLine: true,
};

type TableField = {
  columnLabel: string;
  getValue: (report: SalesLiftSummarySlideV2Props['report']) => string;
  key: string;
};

const tableFields = [
  {
    columnLabel: 'Projected Incremental Sales',
    getValue: report =>
      formatters.currency(report.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: report =>
      formatters.currency(report.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Total Sales Rate',
    getValue: report => formatters.currency(report.totalSalesRate),
    key: 'totalSalesRate',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: report => formatters.currency(report.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: report =>
      formatters.percentage(report.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: report =>
      cpgFormatters.formatAveragePurchaseFrequency(
        report.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: report => formatters.currency(report.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
] as const satisfies readonly TableField[];

type SalesLiftSummarySlideV2Props = {
  slideMaster: string;
  report: Pick<
    CpgReport,
    | 'incrementalSalesLift'
    | 'incrementalSalesLiftConfidenceLevel'
    | 'projectedIncrementalSales'
    | 'totalProjectedSales'
    | 'totalSalesRate'
    | 'averagePricePerTransaction'
    | 'penetration'
    | 'averagePurchaseFrequency'
    | 'averageTotalBasketAmount'
  >;
  roas: ValueInput;
  iroas: ValueInput;
  excludedColumns: string[];
};

export const salesLiftSummarySlideV2 = (
  props: SalesLiftSummarySlideV2Props
): Slide => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  const tableHeaderOptions = {
    color: themeV2.colors.inmarketGreen,
    bold: true,
    fontFace: DEFAULT_FONT,
    fontSize: 11,
  };

  const tableCellOptions = {
    color: themeV2.colors.metricBlue,
    fontFace: DEFAULT_FONT,
    fontSize: 11,
  };

  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTableV2(
        [
          { text: '', options: tableHeaderOptions },
          { text: 'Results', options: tableHeaderOptions },
        ],
        filteredTableFields.map(field => [
          {
            text: field.columnLabel,
            options: tableCellOptions,
          },
          {
            text: field.getValue(props.report),
            options: tableCellOptions,
          },
        ]),
        {
          x: 7.37,
          y: 2.12,
          w: 5.26,
          colW: [2.63, 2.63],
          rowH: 0.45,
        }
      ),
    ],
    texts: [
      contentSlideHeaderV2('Sales Lift Summary'),
      {
        text: [
          {
            text: 'Incremental Sales Lift',
            options: metricLabelOptions,
          },
          {
            text: formatters.lift(props.report.incrementalSalesLift),
            options: metricValueOptions,
          },
          {
            text: `Confidence Level: ${formatters.confidenceLevel(
              props.report.incrementalSalesLiftConfidenceLevel,
              props.report.incrementalSalesLift
            )}`,
            options: metricDescriptionOptions,
          },
        ],
        options: {
          x: 0.33,
          y: 1.8,
          w: 6.25,
          h: 1.5,
          valign: 'top',
          lineSpacingMultiple: 1.3,
        },
      },
      {
        text: [
          {
            text: 'iROAS ',
            options: { ...metricLabelOptions, bold: true, breakLine: false },
          },
          {
            text: '(Incremental Return on Ad Spend)',
            options: {
              ...metricLabelOptions,
              bold: false,
            },
          },
          {
            text: formatters.currency(props.iroas),
            options: metricValueOptions,
          },
        ],
        options: {
          x: 0.33,
          y: 3.3,
          w: 6.25,
          h: 1.2,
          valign: 'top',
          lineSpacingMultiple: 1.3,
        },
      },
      {
        text: [
          {
            text: 'ROAS ',
            options: { ...metricLabelOptions, bold: true, breakLine: false },
          },
          {
            text: '(Return on Ad Spend)',
            options: {
              ...metricLabelOptions,
              bold: false,
            },
          },
          {
            text: formatters.currency(props.roas),
            options: metricValueOptions,
          },
        ],
        options: {
          x: 0.33,
          y: 4.6,
          w: 6.25,
          h: 1.2,
          valign: 'top',
          lineSpacingMultiple: 1.3,
        },
      },
    ],
  };
};
